import {useEffect, useMemo, useState} from "react";
import {App as AntdApp, Select, Alert, Empty, Tag} from "antd";
import className from "classnames/bind";
import {Button, FontAwesomeIcon, Loading} from "~/components";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {useGetProjectQuery} from "~/reduxs/api/projectApiSlice";
import {
	useGetGscSitesQuery,
	useSelectGscPropertyMutation,
	useDisconnectGscMutation,
} from "~/reduxs/api/gscApiSlice";
import style from "../style/Gsc.module.scss";

const cn = className.bind(style);

/** Chuẩn hoá về domain trần để so khớp: hạ chữ thường, bỏ khoảng trắng, bỏ tiền tố "www.". */
const bareDomain = (v) => String(v || '').trim().toLowerCase().replace(/^www\./, '');

/**
 * Property GSC có khớp tên miền dự án không? (mirror `GscController::propertyMatchesDomain`)
 *  - "sc-domain:X" khớp nếu domain dự án BẰNG X hoặc là subdomain của X.
 *  - URL-prefix khớp nếu host trùng domain dự án. Dự án chưa có domain → coi như khớp hết.
 */
const propertyMatchesDomain = (siteUrl, projectDomain) => {
	const domain = bareDomain(projectDomain);
	if (!domain) return true;
	if (siteUrl.startsWith('sc-domain:')) {
		const base = bareDomain(siteUrl.slice('sc-domain:'.length));
		return !!base && (domain === base || domain.endsWith('.' + base));
	}
	try {
		return bareDomain(new URL(siteUrl).hostname) === domain;
	} catch (e) {
		return false;
	}
};

/**
 * Sau khi kết nối, chọn property (site) để gắn vào dự án. Danh sách lấy từ BE
 * (sites.list qua access token). Property phải KHỚP tên miền dự án (BE cũng chặn) — mặc
 * định chỉ hiện property khớp, tự chọn nếu chỉ có 1. Chọn xong → BE đặt mốc backfill 16
 * tháng và job nền bắt đầu đồng bộ.
 */
function GscPropertyPicker({projectId, email, onCancel}) {

	const {notification} = AntdApp.useApp();
	const {data: sites = [], isLoading, isError, error} = useGetGscSitesQuery(projectId);
	const {data: project} = useGetProjectQuery(projectId);
	const [selectProperty, {isLoading: saving}] = useSelectGscPropertyMutation();
	const [disconnect] = useDisconnectGscMutation();
	const [property, setProperty] = useState(null);
	const [showAll, setShowAll] = useState(false);

	const domain = project?.domain || '';

	const matched = useMemo(
		() => sites.filter((s) => propertyMatchesDomain(s.siteUrl, domain)),
		[sites, domain]
	);

	// Chỉ có đúng 1 property khớp → tự chọn cho nhanh.
	useEffect(() => {
		if (matched.length === 1 && !property) setProperty(matched[0].siteUrl);
	}, [matched, property]);

	const hasMatch     = matched.length > 0;
	const visibleSites = (hasMatch && !showAll) ? matched : sites;

	const linkBtn = {background: 'none', border: 0, padding: 0, color: '#2563eb', cursor: 'pointer', textDecoration: 'underline', font: 'inherit'};

	const handleSave = async () => {
		if (!property) {
			notification.warning({message: 'Chưa chọn', description: 'Vui lòng chọn một property.'});
			return;
		}
		try {
			const res = await selectProperty({projectId, property_url: property}).unwrap();
			if (res?.full_backfill === false) {
				notification.warning({
					message: 'Đã chọn property',
					description: 'Dữ liệu mới sẽ cập nhật hàng ngày, nhưng lịch sử 16 tháng tạm hoãn do tên miền vừa được đồng bộ gần đây.',
				});
			} else {
				notification.success({message: 'Đã chọn property', description: 'Hệ thống đang đồng bộ dữ liệu trong nền.'});
			}
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không lưu được property')});
		}
	};

	const handleDisconnect = async () => {
		try {
			await disconnect(projectId).unwrap();
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không ngắt được kết nối')});
		}
	};

	if (isLoading) return <Loading />;

	return (
		<div className={cn('connectPanel')}>
			<div className={cn('connectIcon')}>
				<FontAwesomeIcon icon="fa-light fa-list-check" />
			</div>
			<div className={cn('connectTitle')}>Chọn property</div>
			<div className={cn('connectDesc')}>
				{email ? <>Đã kết nối bằng <strong>{email}</strong>. </> : null}
				Chọn website (property) trong Search Console để đồng bộ dữ liệu cho dự án này
				{domain ? <> — property phải thuộc tên miền <strong>{domain}</strong>.</> : '.'}
			</div>

			{isError && (
				<Alert type="error" showIcon style={{textAlign: 'left', marginBottom: 16}}
					message="Không lấy được danh sách property"
					description={rtkErrorMessage(error, 'Vui lòng thử ngắt kết nối và kết nối lại.')} />
			)}

			{!isError && sites.length === 0 && (
				<Empty description="Tài khoản Google này chưa có property nào trong Search Console." style={{marginBottom: 16}} />
			)}

			{/* Không có property nào khớp tên miền dự án → cảnh báo (BE sẽ chặn nếu chọn sai). */}
			{!isError && sites.length > 0 && domain && !hasMatch && (
				<Alert type="warning" showIcon style={{textAlign: 'left', marginBottom: 16}}
					message={`Không tìm thấy property khớp tên miền ${domain}`}
					description="Tài khoản Google này không có property nào thuộc tên miền của dự án. Hãy xác minh quyền sở hữu tên miền trong Search Console, hoặc tạo dự án mới đúng với property bạn có." />
			)}

			{/* Có property khớp nhưng đang ẩn bớt các property khác → cho phép hiện tất cả. */}
			{hasMatch && sites.length > matched.length && (
				<div style={{textAlign: 'left', marginBottom: 12, fontSize: 13}}>
					{showAll
						? <>Đang hiện tất cả property. <button type="button" style={linkBtn} onClick={() => setShowAll(false)}>Chỉ hiện property khớp tên miền</button></>
						: <>Chỉ hiện property khớp <strong>{domain}</strong>. <button type="button" style={linkBtn} onClick={() => setShowAll(true)}>Hiện tất cả property</button></>}
				</div>
			)}

			{sites.length > 0 && (
				<Select
					style={{width: '100%', maxWidth: 460, marginBottom: 20, textAlign: 'left'}}
					placeholder="Chọn property…"
					value={property}
					onChange={setProperty}
					options={visibleSites.map((s) => {
						const isMatch = propertyMatchesDomain(s.siteUrl, domain);
						return {
							value: s.siteUrl,
							label: (
								<span>
									{s.siteUrl}{'  '}
									<span style={{color: '#888'}}>({s.permissionLevel})</span>
									{showAll && domain && isMatch && <Tag color="green" style={{marginLeft: 8}}>khớp</Tag>}
								</span>
							),
						};
					})}
				/>
			)}

			<div style={{display: 'flex', gap: 10, justifyContent: 'center'}}>
				{onCancel
					? <Button white onClick={onCancel}>Huỷ</Button>
					: <Button white onClick={handleDisconnect}>Ngắt kết nối</Button>}
				{sites.length > 0 && (
					<Button primary loading={saving} onClick={handleSave}
						leftIcon={<FontAwesomeIcon icon="fa-light fa-check" />}>
						Bắt đầu đồng bộ
					</Button>
				)}
			</div>
		</div>
	);
}

export default GscPropertyPicker;
